import { call, put, takeEvery } from "redux-saga/effects";
import type { PayloadAction } from "@reduxjs/toolkit";
import { message } from "antd";
import request from "../../../utils/request";
import { API_ENDPOINT } from "../../../utils/constants";
import { extractErrorMessage, handleAxiosError } from "../../../utils/APIValidations";
import { clearAuthSession, updateStoredUserName } from "../../../utils/auth";
import { userProfileUpdated } from "../Login/slice";
import { navigateTo } from "../../../utils/navigation";
import {
  loadProfile,
  profileLoaded,
  loadProfileError,
  loadProfilePicture,
  profilePictureLoaded,
  loadProfilePictureError,
  savePassword,
  passwordSaved,
  savePasswordError,
  saveProfile,
  profileSaved,
  saveProfileError,
  showEmailVerifyModal,
  verifyEmailChange,
  emailChangeVerified,
  verifyEmailChangeError,
} from "./slice";
import type {
  ChangePasswordPayload,
  EditProfilePayload,
  UserProfile,
  VerifyEmailChangePayload,
} from "./types";

export function* fetchProfile() {
  try {
    const response: UserProfile = yield call(request, `${API_ENDPOINT}profile`);
    yield put(profileLoaded(response));
  } catch (e) {
    yield call(handleAxiosError, e);
    yield put(loadProfileError());
  }
}

export function* fetchProfilePicture() {
  try {
    const response: { id: number; profile_image: string } = yield call(
      request,
      `${API_ENDPOINT}profile/picture`,
    );
    yield put(profilePictureLoaded(response.profile_image));
  } catch (e) {
    yield call(handleAxiosError, e);
    yield put(loadProfilePictureError());
  }
}

export function* postChangePassword({
  payload,
}: PayloadAction<ChangePasswordPayload>) {
  const options: RequestInit = {
    mode: "cors",
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  };
  try {
    yield call(request, `${API_ENDPOINT}change-password`, options);
    yield put(passwordSaved());
    message.success("Password changed successfully. Please log in again.");
    clearAuthSession();
    navigateTo("/login");
  } catch (e) {
    yield call(handleAxiosError, e);
    yield put(savePasswordError());
  }
}

export function* postEditProfile({
  payload,
}: PayloadAction<EditProfilePayload>) {
  const options: RequestInit = {
    mode: "cors",
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  };
  try {
    yield call(request, `${API_ENDPOINT}profile/edit`, options);
    updateStoredUserName(payload.full_name);
    yield put(profileSaved({ full_name: payload.full_name }));
    yield put(userProfileUpdated({ full_name: payload.full_name }));
    if (payload.email) {
      yield put(showEmailVerifyModal(payload.email));
    } else {
      message.success("Profile updated successfully.");
      yield put(loadProfile());
    }
  } catch (e) {
    yield call(handleAxiosError, e);
    yield put(saveProfileError());
  }
}

export function* postVerifyEmailChange({
  payload,
}: PayloadAction<VerifyEmailChangePayload>) {
  const options: RequestInit = {
    mode: "cors",
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  };
  try {
    yield call(request, `${API_ENDPOINT}verify-email-change`, options);
    yield put(emailChangeVerified());
    message.success("Email updated. Please log in again.");
    clearAuthSession();
    navigateTo("/login");
  } catch (e) {
    const msg = extractErrorMessage(e);
    message.error(msg);
    yield put(verifyEmailChangeError(msg));
  }
}

export default function* saga() {
  yield takeEvery(loadProfile.type, fetchProfile);
  yield takeEvery(loadProfilePicture.type, fetchProfilePicture);
  yield takeEvery(savePassword.type, postChangePassword);
  yield takeEvery(saveProfile.type, postEditProfile);
  yield takeEvery(verifyEmailChange.type, postVerifyEmailChange);
}
